import React, { useEffect, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import { useVideo } from "../../../context/VideoContext";
import { useWordTranslation } from "../../../hooks/useWordTranslation";
import TranslationSection from "./TranslationSection";
import DefinitionSection from "./DefinitionSection";
import ExternalDictBar from "./ExternalDictBar";
import SaveWordButton from "./SaveWordButton";
import { useNotification } from "../../../context/NotificationContext";
import { usePackagesModal } from "../../../context/PackagesModalContext";
import "./WordInfoModal.css";

const formatDictToText = (dict) => {
  if (!dict || dict.length === 0) return "";
  return dict
    .map((item) => {
      const pos = item.pos || "nopos";
      const translations = (item.translations || []).join("، ");
      return `${pos} : ${translations}`;
    })
    .join("\n");
};

const WordInfoModal = ({
  isOpen,
  word = "",
  fullText = "",
  onClose,
  fromLang = "en",
  toLang = "fa",
  movieName = "",
  season = "",
  episode = "",
  subtitle1Text = "",
  subtitle2Text = "",
  isSentenceMode = false,
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const { showPackages } = usePackagesModal();
  const [wasVideoFullscreenBeforePopup, setWasVideoFullscreenBeforePopup] =
    useState(false);
  const [translationData, setTranslationData] = useState(null);
  const [loadingState, setLoadingState] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [editableWord, setEditableWord] = useState(word);
  const [editableMeanings, setEditableMeanings] = useState("");
  const [isModalFullscreen, setIsModalFullscreen] = useState(false);
  const [editText, setEditText] = useState("");
  const [isDictBarVisible, setIsDictBarVisible] = useState(true);

  const {
    setShortcutsEnabled,
    toggleFullscreen: toggleVideoFullscreen,
    isFullscreen: isVideoFullscreen,
    isIOS,
    containerRef,
    setIsModalOpen,
  } = useVideo();

  const { getTranslation, isPremium, remainingQuota } = useWordTranslation();
  const { showError } = useNotification();

  const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;

  useEffect(() => {
    setEditableWord(word);
  }, [word]);

  useEffect(() => {
    if (isOpen && word) {
      fetchTranslation();
    }
  }, [isOpen, word, fromLang]);

  useEffect(() => {
    if (isOpen) {
      setIsModalOpen(true);
      document.body.style.overflow = "hidden";
      setShortcutsEnabled(false);
      setIsDictBarVisible(true);
    }

    return () => {
      setIsModalOpen(false);
      document.body.style.overflow = "";
      setShortcutsEnabled(true);

      if (wasVideoFullscreenBeforePopup && !isVideoFullscreen) {
        toggleVideoFullscreen();
      }
    };
  }, [
    isOpen,
    setShortcutsEnabled,
    wasVideoFullscreenBeforePopup,
    isVideoFullscreen,
    toggleVideoFullscreen,
    setIsModalOpen,
  ]);

  useEffect(() => {
    if (translationData?.dict) {
      const formatted = formatDictToText(translationData.dict);
      setEditableMeanings(formatted);
      setEditText(formatted);
    }
  }, [translationData]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen && !showPackages) {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, showPackages]);

  const fetchTranslation = async () => {
    setLoadingState("loading");
    setErrorMessage("");

    try {
      const result = await getTranslation(fromLang, toLang, word);

      if (result?.data) {
        setTranslationData(result.data);
        setLoadingState("success");
      } else if (result?.error === "QUOTA_EXCEEDED") {
        setLoadingState("quota_exceeded");
        setErrorMessage(result.message);
      } else if (result?.error) {
        setLoadingState("error");
        setErrorMessage(result.message || "خطا در دریافت ترجمه");
        showError(result.message || "خطا در دریافت ترجمه", {
          duration: 4000,
          position: "top-center",
        });
      }
    } catch (error) {
      console.error("Translation fetch failed:", error);
      setLoadingState("error");
      setErrorMessage("خطا در ارتباط با سرور. لطفاً دوباره تلاش کنید.");
    }
  };

  const handleClose = () => {
    setIsEditMode(false);
    setWasVideoFullscreenBeforePopup(false);
    onClose();
  };

  const handleExternalDictClick = () => {
    setWasVideoFullscreenBeforePopup(isVideoFullscreen);
    if (isVideoFullscreen) {
      toggleVideoFullscreen();
    }
    setIsEditMode(true);
  };

  const toggleModalFullscreen = () => {
    setIsModalFullscreen((prev) => !prev);
  };

  const handleRequirePremium = (message) => {
    showPackages({
      onSelect: (pkg) => {
      },
    });
    showError(message, {
      duration: 4000,
      position: "top-center",
    });
  };

  const handleSaveComplete = () => {
    handleClose();
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setEditText((prev) => {
          const trimmed = prev.trim();
          return trimmed ? trimmed + "\n--------\n" + text : text;
        });
      }
    } catch {
      showError("خطا در خواندن کلیپ‌بورد", {
        duration: 3000,
        position: "top-center",
      });
    }
  };

  const getPortalContainer = useCallback(() => {
    if (isIOS && isVideoFullscreen && containerRef?.current) {
      return containerRef.current;
    }

    if (!isIOS && isVideoFullscreen) {
      const fullscreenElement =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;

      if (fullscreenElement) {
        return fullscreenElement;
      }
    }

    return document.body;
  }, [isIOS, isVideoFullscreen, containerRef]);

  if (!isOpen || !word) return null;

  const portalContainer = getPortalContainer();

  const modalContent = (
    <div
      className={`wim-overlay ${
        isVideoFullscreen ? "wim-overlay--fullscreen" : ""
      }`}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        handleClose();
      }}
      onTouchStart={(e) => e.stopPropagation()}
      onTouchEnd={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div
        className={`wim-panel ${isModalFullscreen ? "wim-fullscreen" : ""}`}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="wim-header">
          <div className="wim-header-left">
            <span className="wim-header-icon">
              <i className="fas fa-book-open"></i>
            </span>
            <div className="wim-header-title">
              <h3>اطلاعات لغت</h3>
              {!isPremium && (
                <div className="wim-quota">
                  <i className="fas fa-bolt"></i>
                  {remainingQuota(fromLang)}/{10} رایگان
                </div>
              )}
            </div>
          </div>
          <div className="wim-header-actions">
            <button
              className="wim-btn wim-btn-icon wim-btn-dict"
              onClick={() => setIsDictBarVisible(!isDictBarVisible)}
              title={
                isDictBarVisible ? "مخفی کردن دیکشنری‌ها" : "نمایش دیکشنری‌ها"
              }
            >
              <i
                className={`fas ${isDictBarVisible ? "fa-book-open" : "fa-book"}`}
              ></i>
            </button>
            <button
              className="wim-btn wim-btn-icon wim-btn-fullscreen"
              onClick={toggleModalFullscreen}
              title={isModalFullscreen ? "حالت عادی" : "تمام صفحه"}
            >
              <i
                className={`fas ${
                  isModalFullscreen ? "fa-compress" : "fa-expand"
                }`}
              ></i>
            </button>
            <button
              className="wim-btn wim-btn-icon wim-btn-close"
              onClick={handleClose}
              title="بستن"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        {isDictBarVisible && (
          <ExternalDictBar
            fromLang={fromLang}
            toLang={toLang}
            word={word}
            onOpenPopup={handleExternalDictClick}
          />
        )}

        {remainingQuota(fromLang) == 0 ? (
          <div className="wim-content">
            <div className="wim-sentence">
              محدودیت در استفاده روزانه، لطفا اشتراک تهیه کنید
            </div>
            <div
              className={`wim-word`}
              style={{ display: "flex", justifyContent: "center" }}
            >
              <button
                className="wim-btn wim-btn-edit"
                onClick={() =>
                  handleRequirePremium("سهمیه رایگان شما تمام شده است")
                }
              >
                دریافت اشتراک
              </button>
            </div>
          </div>
        ) : (
          <div className="wim-content">
            <div className="wim-word-section">
              <div className="wim-section-label">
                {isSentenceMode ? "جمله:" : "کلمه:"}
              </div>
              {isEditMode ? (
                <input
                  type="text"
                  value={editableWord}
                  onChange={(e) => setEditableWord(e.target.value)}
                  className="wim-edit-input"
                />
              ) : (
                <div
                  className={`wim-word ${
                    isSentenceMode ? "wim-word--sentence" : ""
                  }`}
                >
                  {isSentenceMode ? fullText : word}
                </div>
              )}
            </div>

            <div className="wim-trans-section">
              <div className="wim-section-label">
                {isSentenceMode ? "ترجمه:" : "ترجمه‌ها:"}
              </div>
              {isSentenceMode ? (
                <div className="wim-sentence">
                  {subtitle2Text || "ترجمه‌ای موجود نیست"}
                </div>
              ) : (
                <>
                  {loadingState === "success" && translationData && (
                    isEditMode ? (
                      <div className="wim-edit-area">
                        <textarea
                          className="wim-edit-textarea"
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          dir="rtl"
                          placeholder="particle : ترجمه1، ترجمه2"
                        />
                        <button
                          className="wim-paste-btn"
                          onClick={handlePaste}
                          title="چسباندن از کلیپ‌بورد"
                          type="button"
                        >
                          <i className="fas fa-paste"></i>
                        </button>
                      </div>
                    ) : (
                      <TranslationSection data={translationData} />
                    )
                  )}
                </>
              )}
            </div>

            {loadingState === "success" && translationData?.enData && (
              <DefinitionSection enData={translationData.enData} />
            )}
          </div>
        )}

        <div className="wim-footer">
          {remainingQuota(fromLang) == 0 ? null : (
            <div className="wim-footer-left">
              <button
                className="wim-btn wim-btn-edit"
                onClick={() => {
                  if (isEditMode) {
                    setEditText(editableMeanings);
                  }
                  setIsEditMode(!isEditMode);
                }}
              >
                {isEditMode ? "انصراف از ویرایش" : "ویرایش و ذخیره"}
              </button>

              <SaveWordButton
                word={isEditMode ? editableWord : word}
                fromLang={fromLang}
                toLang={toLang}
                meanings={isEditMode ? editText : editableMeanings}
                subtitle={subtitle1Text}
                translateOfSubtitle={subtitle2Text}
                movieName={movieName}
                season={season}
                episode={episode}
                isPremium={isPremium}
                onRequirePremium={handleRequirePremium}
                onSaveComplete={handleSaveComplete}
              />
            </div>
          )}

          <button
            className="wim-btn wim-btn-close"
            onClick={handleClose}
          >
            بستن
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, portalContainer);
};

export default WordInfoModal;
